<template>
  <div class="view-orders-cnt">
    <BannerCarousel
      v-if="showSelfSellInBanner"
      class="banner-cnt"
      :section-type="sectionType"
      :scroll-target-id="'orders'" />

    <div id="orders" class="view-orders">
      <div class="form">
        <div class="title-cnt">
          <h3 class="page-title" data-cy="orders-title">{{ $t('viewOrders.title') }} {{ totalRows }}</h3>
          <div class="action-buttons">
            <router-link to="/launch-calendar" data-cy="launch-calendar-button">
              <BackstageButton icon-right="calendar">
                {{ $t('viewLaunchCalendar.launch_calendar_button') }}
              </BackstageButton>
            </router-link>
            <router-link
              v-if="isReOrderEnabled"
              :to="{ name: productsAvailabilityRouteName }"
              data-cy="check-availability-button">
              <BackstageButton>
                {{ $t('viewOrders.check_availability_button') }}
              </BackstageButton>
            </router-link>
            <BackstageButton
              icon-right="addition-sign"
              :disabled="salesOrdersDisabled"
              data-cy="place-new-order-button-cnt"
              data-cy-button="place-new-order-button"
              v-on:click="showOrderCreatePopUp = true">
              {{ $t('viewOrders.place_new_order_button') }}
            </BackstageButton>
          </div>
        </div>
        <div class="nav-pills" data-cy="orders-table-options">
          <nav>
            <a
              v-for="option in viewOptions"
              :key="option"
              class="link"
              :class="{ selected: option === currentViewOption }"
              :data-cy="`viewOrders.viewOptions.${option}`"
              v-on:click="selectViewOption(option)">
              <span>{{ $t(`viewOrders.viewOptions.${option}`) }}</span>
            </a>
          </nav>
          <div class="buttons-menu">
            <BackstageButton
              v-if="shouldShowItems()"
              :disabled="disabledButton"
              :variant="ButtonVariant.SECONDARY"
              :icon-left="downloadingCsv ? 'loading' : 'xlsx-file'"
              data-cy="download-backorders-button-cnt"
              data-cy-button="download-backorders-button"
              v-on:click="downloadBackOrderItems()">
              {{ $t('viewOrders.download.button_label') }}
            </BackstageButton>
            <BackstageButton
              v-if="totalRows"
              :variant="ButtonVariant.SECONDARY"
              icon-left="trash"
              data-cy="clear-all-filters-button-cnt"
              data-cy-button="clear-all-filters-button"
              v-on:click="clearGridFilters()">
              {{ $t('global.grids.clear_all_filters_button') }}
            </BackstageButton>
            <div v-if="shouldShowOrders()" class="actions-menu-wrapper">
              <BackstageButton
                v-if="!selectedActionItem"
                :variant="ButtonVariant.SECONDARY"
                :disabled="isActionDropdownButtonDisabled"
                icon-right="chevron-down"
                data-cy="actions-button-cnt"
                data-cy-button="actions-button"
                v-on:click="toggleActionsDropdown">
                {{ $t('global.actions_button') }}
              </BackstageButton>
              <BackstageDropdownMenu
                class="dropdown-menu"
                :visible="isActionsDropdownVisible"
                v-on:update:visible="isActionsDropdownVisible = $event"
                v-on:item-selected="selectAction"
                :items="actionsDropdownItems" />
              <BackstageButton
                v-if="selectedActionItem"
                :disabled="isActionButtonDisabled"
                :variant="ButtonVariant.SECONDARY"
                :icon-right="actionLoading ? 'loading' : undefined"
                data-cy="select-to-duplicate-button-cnt"
                data-cy-button="select-to-duplicate-button"
                v-on:click="performAction">
                {{ selectedActionItem?.name }}
              </BackstageButton>
            </div>
            <BackstageButton
              v-if="selectedActionItem && shouldShowOrders()"
              :variant="ButtonVariant.SECONDARY"
              data-cy="cancel-button-cnt"
              data-cy-button="cancel-button"
              v-on:click="cancelFromAction()">
              {{ $t('global.cancel_button') }}
            </BackstageButton>
          </div>
        </div>
        <OrdersGrid
          v-if="shouldShowOrders()"
          ref="orders"
          :draft-id="draftId"
          :order-id="orderId"
          :checkbox-selection="checkboxSelection"
          :selected-action-items="selectedActionItem"
          v-on:on-selection-changed="onSelectionChanged"
          v-on:update-row-count="updateTotalRows"
          v-on:grid-filter-update="updateOrderOverview"
          v-on:grid-loading-done="gridLoaded()" />
        <UnshippedItemsGrid
          v-if="shouldShowItems()"
          ref="items"
          v-on:update-row-count="updateTotalRows"
          v-on:backorder-items-loaded="enableBackorderDownloadButton" />
        <NotificationMessage
          v-if="showOrderRestrictionPopUp"
          :size="orderRestrictionPopUpSize"
          :type="orderRestrictionPopUpType"
          v-on:close="closeOrderRestrictionPopUp">
          <template v-slot:default>
            <div v-if="isSelectionDuplicate" v-html="t('viewOrders.order_duplication.orders_restriction_note')"></div>
            <div v-if="isSelectionDownload" v-html="t('viewOrders.download.order_selection_exceeded')"></div>
          </template>
        </NotificationMessage>
      </div>
    </div>
    <OrderOverview
      v-if="shouldShowOrders()"
      :from="orderOverview.from"
      :to="orderOverview.to"
      :total-items="orderOverview.totalItems"
      :filter-type="orderOverview.filterType"
      :order-numbers="orderOverview.orderNumbers" />

    <div v-if="showOrderCreatePopUp">
      <NotificationMessage
        :title="$t('createOrdersPopup.order_types_title')"
        :size="'medium'"
        v-on:close="showOrderCreatePopUp = false">
        <InitNewOrder v-on:close="showOrderCreatePopUp = false" />
      </NotificationMessage>
    </div>

    <div v-if="showSelectableFieldsPopup">
      <NotificationMessage
        size="large"
        :show-close-button="true"
        :show-outside-close-button="false"
        class="selectable-fields-popup"
        @close="showSelectableFieldsPopup = false">
        <SelectableFields @export="downloadMultipleExcel" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageDropdownMenu from '@/components/common/BackstageDropdownMenu.vue';
import { useAnalytics } from '@/composables/analytics';
import { useBPLogger } from '@/composables/business-process-logger';
import { downloadXlsxFile } from '@/composables/download';
import { SELECTABLE_FIELDS_DISABLED_ORDER_TYPES } from '@/constants/order.constants';
import { utilsMixins } from '@/mixins/utils.mixins';
import { ButtonVariant } from '@/models/button-variant.enum';
import type { ActionDropdownItem } from '@/models/dropdown-item';
import { Feature } from '@/models/feature.model';
import { Icons } from '@/models/icons.enum';
import { PageEnum } from '@/models/page.enum';
import { PopUpSize } from '@/models/pop-up-size.enum';
import { PopUpType } from '@/models/pop-up-type.enum';
import { SelectableActions } from '@/models/selectable-actions.enum';
import { OrderItemsService } from '@/services/order-items.service';
import { OrdersService } from '@/services/orders.service';
import { useDraftsInitInfoStore } from '@/stores/drafts-init-info';
import { useSelectedOrdersStore } from '@/stores/selected-grid-orders';
import { useUserStore } from '@/stores/user';
import { defaultSelectedLineFields, type OrderHeaderModel } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useI18n, type Composer } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { GaCustomEvents } from '../../models/ga-custom-events.enum';
import { OrderOverviewModel } from '../../models/order-overview.model';
import { RouteNames } from '../../router';
import { FeaturesService } from '../../services/features.service';
import BannerCarousel from '../common/BackstageBannerCarousel.vue';
import NotificationMessage from '../common/NotificationMessage.vue';
import InitNewOrder from './init-new-order.vue';
import OrderOverview from './order-overview.vue';
import OrdersGrid from './orders-grid.vue';
import SelectableFields from './selectable-fields.vue';
import UnshippedItemsGrid from './unshipped-items-grid.vue';

enum ViewOptions {
  Orders = 'orders',
  Items = 'items',
}

const ORDERS_DUPLICATION_LIMIT = 10;
const ORDERS_CSV_DOWNLOAD_LIMIT = 500;

export default defineComponent({
  name: 'view-orders',
  components: {
    OrdersGrid,
    UnshippedItemsGrid,
    BannerCarousel,
    OrderOverview,
    BackstageButton,
    NotificationMessage,
    InitNewOrder,
    BackstageDropdownMenu,
    SelectableFields,
  },
  props: {
    orderId: {
      type: String,
      default: undefined,
    },
    draftId: {
      type: String,
      default: undefined,
    },
    show: {
      type: String,
      default: ViewOptions.Orders,
    },
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { selectedAccountId: accountId, draftInitInfo } = storeToRefs(useUserStore());
    const { isReOrderEnabled } = storeToRefs(useDraftsInitInfoStore());
    const { removeSelectedOrders, getSelectedOrders } = useSelectedOrdersStore();
    const { t } = useI18n();
    const { pageViewBPLog } = useBPLogger();
    const { addCustomGaEvent } = useAnalytics();

    return {
      addCustomGaEvent,
      route,
      router,
      accountId,
      draftInitInfo,
      isReOrderEnabled,
      ButtonVariant,
      t: t as Composer['t'],
      getSelectedOrders,
      removeSelectedOrders,
      downloadXlsxFile,
      pageViewBPLog,
    };
  },
  data() {
    return {
      ordersService: new OrdersService(),
      sectionType: PageEnum.homePage,
      selectedOrderId: undefined as any,
      selectedOrderIsDraft: false,
      salesOrdersDisabled: true,
      featuresService: new FeaturesService(),
      viewOptions: Object.values(ViewOptions),
      currentViewOption: this.show,
      totalRows: '',
      orderItemsService: new OrderItemsService(),
      disabledButton: true,
      downloadingCsv: false,
      showOrderCreatePopUp: false,
      showSelectableFieldsPopup: false,
      orderOverview: new OrderOverviewModel(),
      productsAvailabilityRouteName: RouteNames.ProductsAvailabilityDefault,
      orderDuplicationEnabled: false,
      orderDownloadEnabled: false,
      showOrderRestrictionPopUp: false,
      orderRestrictionPopUpSize: PopUpSize.Small,
      orderRestrictionPopUpType: PopUpType.Warning,
      selectedRows: new Array<any>(),
      gridLoading: true,
      isActionsDropdownVisible: false,
      actionLoading: false,
      selectedActionItem: undefined as ActionDropdownItem | undefined,
      showSelfSellInBanner: FeaturesService.enableSelfSellIn,
    };
  },
  watch: {
    accountId(newVal) {
      if (newVal) {
        this.gridLoading = true;
        this.determineWhetherSalesOrdersAreDisabled(newVal);
      }
    },
  },
  beforeMount() {
    this.currentViewOption = this.show;
  },
  mounted() {
    this.removeSelectedOrders();
    this.determineWhetherSalesOrdersAreDisabled(this.accountId as string);

    if (this.orderId) {
      this.selectedOrderId = this.orderId;
    }

    if (this.draftId) {
      this.selectedOrderId = this.draftId;
      this.selectedOrderIsDraft = true;
    }
    console.log(this.pageViewBPLog(true, { page_name: 'view-orders' }));
  },
  methods: {
    ...utilsMixins,
    async determineWhetherSalesOrdersAreDisabled(accountId: string) {
      this.salesOrdersDisabled = await this.featuresService.hasFeature(Feature.SALES_ORDERS_DISABLED, accountId);
    },
    cancelFromAction() {
      this.selectedActionItem = undefined;
    },
    gridLoaded() {
      this.gridLoading = false;
    },
    isDuplicationFeatureEnabled() {
      return this.currentViewOption === ViewOptions.Orders;
    },
    closeOrderRestrictionPopUp() {
      this.showOrderRestrictionPopUp = false;
    },
    onSelectionChanged(selectedRows: any[]) {
      if (this.isSelectionDuplicate) {
        this.checkDuplicationLimit(selectedRows);
      }

      if (this.isSelectionDownload) {
        this.checkDownloadExcelLimit(selectedRows);
      }

      this.selectedRows = selectedRows;
    },
    checkDuplicationLimit(selectedRows: any[]) {
      this.orderDuplicationEnabled = selectedRows.length > 0 && selectedRows.length <= ORDERS_DUPLICATION_LIMIT;

      this.shouldShowToast(selectedRows, ORDERS_DUPLICATION_LIMIT);
    },
    checkDownloadExcelLimit(selectedRows: any[]) {
      this.orderDownloadEnabled = selectedRows.length > 0 && selectedRows.length <= ORDERS_CSV_DOWNLOAD_LIMIT;

      this.shouldShowToast(selectedRows, ORDERS_CSV_DOWNLOAD_LIMIT);
    },
    shouldShowToast(selectedRows: any[], limit: number) {
      if (selectedRows.length > this.selectedRows.length && selectedRows.length > limit) {
        const message = this.isSelectionDuplicate
          ? this.$t('viewOrders.order_duplication.orders_restriction_note')
          : this.$t('viewOrders.download.order_selection_exceeded');

        this.$toast.show({
          message,
          duration: 5000,
        });
      }
    },
    shouldShowOrders() {
      return this.currentViewOption === ViewOptions.Orders;
    },
    shouldShowItems() {
      return this.currentViewOption === ViewOptions.Items;
    },
    selectViewOption(option: string) {
      this.currentViewOption = option;

      if (this.route.query.show !== this.currentViewOption) {
        this.totalRows = '';
        this.router.replace({ query: { ...this.route.query, show: this.currentViewOption } });
        this.attemptAutoScroll('orders', this.route.query);
      }
    },
    updateTotalRows(rowCount: number) {
      this.totalRows = `( ${rowCount} ${this.$t('viewOrders.title-suffix')} )`;
    },
    async downloadBackOrderItems() {
      this.disabledButton = true;
      this.downloadingCsv = true;

      await this.orderItemsService.downloadBackorderItems(this.accountId).then((response) => {
        if (response.isValid()) {
          try {
            const base64Body = response.model?.base64Body;
            if (base64Body) this.downloadXlsxFile(base64Body, `${this.accountId}`);
          } catch (err) {
            this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));

            this.addCustomGaEvent({
              event: GaCustomEvents.FileDownloadError,
              link_text: this.accountId,
            });
          }
        }
      });

      this.disabledButton = false;
      this.downloadingCsv = false;
    },
    enableBackorderDownloadButton(backorderItemsLoaded: boolean) {
      this.disabledButton = !backorderItemsLoaded;
    },
    clearGridFilters() {
      const gridElement: any = this.$refs[this.currentViewOption];

      gridElement.removeAllGridFilters();
      this.orderOverview = new OrderOverviewModel();
    },
    updateOrderOverview(overview: OrderOverviewModel) {
      this.orderOverview = overview;
    },
    duplicateOrder(): void {
      this.$router.push('/orders-duplication');
    },
    getFileDownloadName(selectedOrders: OrderHeaderModel[]): string {
      const fileNamePrefix =
        selectedOrders.length === 1 ? `${selectedOrders[0].orderNumber}` : this.$t('viewOrders.download.file_name');

      return `${fileNamePrefix} ${new Date().toISOString().slice(0, 10)}.xlsx`;
    },
    async downloadMultipleExcel(selectedFields: string[], selectedOrdersParam?: OrderHeaderModel[]) {
      try {
        this.showSelectableFieldsPopup = false;
        this.actionLoading = true;
        const selectedOrders = selectedOrdersParam || this.getSelectedOrders();
        const fileName = this.getFileDownloadName(selectedOrders);

        if (!selectedOrders.length) {
          this.pageViewBPLog(false, {
            page_name: 'view-orders',
            action: 'download-multiple-excel',
            status: 'failed',
            message: 'No orders selected',
          });

          console.error('No orders selected');

          return;
        }

        const orderNumbers = selectedOrders.map((order) => order.orderNumber);

        await this.ordersService.downloadOrder(orderNumbers, this.accountId, fileName, selectedFields);
      } catch (error) {
        this.pageViewBPLog(false, {
          page_name: 'view-orders',
          action: 'download-multiple-excel',
          status: 'failed',
          message: 'Error downloading orders',
        });

        console.error('Error downloading orders', error);
      } finally {
        this.actionLoading = false;
      }
    },
    async handleExcelDownload() {
      const selectedOrders = this.getSelectedOrders();
      if (FeaturesService.selectableFieldsEnabled) {
        const areAllOrdersDisabled = selectedOrders.every((order) =>
          SELECTABLE_FIELDS_DISABLED_ORDER_TYPES.has(order.type),
        );

        if (areAllOrdersDisabled) {
          this.downloadMultipleExcel(Array.from(defaultSelectedLineFields), selectedOrders);

          return;
        }

        this.showSelectableFieldsPopup = true;
      } else {
        this.downloadMultipleExcel(Array.from(defaultSelectedLineFields), selectedOrders);
      }
    },
    toggleActionsDropdown() {
      this.isActionsDropdownVisible = !this.isActionsDropdownVisible;
    },
    selectAction(item: ActionDropdownItem) {
      this.selectedActionItem = item;
      this.isActionsDropdownVisible = false;
    },
    performAction() {
      this.selectedActionItem!.action!();
    },
  },
  computed: {
    actionsDropdownItems(): ActionDropdownItem[] {
      const items: ActionDropdownItem[] = [
        {
          key: SelectableActions.Download,
          name: this.$t('global.download_button'),
          label: this.$t('viewOrders.download_xls_button'),
          action: this.handleExcelDownload,
          icon: this.actionLoading ? Icons.LOADING : undefined,
        },
      ];

      if (this.isDuplicationFeatureEnabled()) {
        items.push({
          key: SelectableActions.Duplicate,
          name: this.$t('global.duplicate_button'),
          label: this.$t('viewOrders.duplicate_order_button'),
          action: this.duplicateOrder,
        });
      }

      return items;
    },
    checkboxSelection() {
      switch (this.selectedActionItem?.key) {
        case SelectableActions.Duplicate:
          return (params: any) => params.data.duplicatable;
        case SelectableActions.Download:
          return () => true;
        default:
          return () => false;
      }
    },
    isSelectionDuplicate() {
      return this.selectedActionItem?.key === SelectableActions.Duplicate;
    },
    isSelectionDownload() {
      return this.selectedActionItem?.key === SelectableActions.Download;
    },
    isActionButtonDisabled() {
      return (
        this.selectedRows.length === 0 ||
        this.selectedActionItem === undefined ||
        (this.isSelectionDuplicate && !this.orderDuplicationEnabled) ||
        (this.isSelectionDownload && !this.orderDownloadEnabled)
      );
    },
    isActionDropdownButtonDisabled(): boolean {
      return !this.totalRows || this.gridLoading || !this.shouldShowOrders();
    },
  },
});
</script>
<style scoped lang="scss">
.view-orders-cnt {
  .banner-cnt {
    margin: auto;
  }

  .view-orders {
    @include common-container;
    min-height: 100vh;
    padding-top: 5px;

    .title-cnt {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-wrap: wrap;

      .action-buttons {
        display: flex;
        gap: 10px;
      }
    }

    .form {
      @include container-background;
    }

    .nav-pills .buttons-menu {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }

    .nav-pills .actions-menu-wrapper {
      position: relative;

      .dropdown-menu {
        right: 0;
        width: 170%;
        position: absolute;
      }
    }
  }
  .selector-button-box {
    display: flex;
  }
  .button {
    margin-top: 7px;
  }
  .button-negative {
    &::before {
      content: none;
    }
  }
  .xlsx-file-button-backorder {
    background-size: 2em;
    background-position-x: 1em;
    float: left;
    height: 5%;
  }
}
</style>
